import { getAuthTokenForAccount } from "./auth/accessToken/observables/authStore";
import { VERSION } from "./env";
import { Causes, errorExternalStore } from "./errors";
import { statusCodes } from "./helpers/httpStatusCodes";
import { expiresAt } from "./helpers/jwt";
import { match } from "./util/match";

type HttpHeaders = {
    [headerName: string]: string;
};

type ApiParams<T, P = unknown> = {
    method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    url: string;
    headers?: HttpHeaders;
    accountId?: string;
    json?: P;
    body?: BodyInit | null;
    cacheBuster?: boolean;
    includeSystemHeaders?: boolean;
    signal?: AbortSignal;
    handlers?: Record<
        number,
        // Input is intentionally `any`: a handler keyed on an error status
        // commonly receives a different body shape than the success type `T`
        // (e.g. `400: (error: ErrorBody) => ...`). The return type stays `T`
        // so `api(...)` still resolves to `Promise<T>`.
        (data: any, headers: Headers, error?: ErrorResponse) => T
    >;
    useErrorStore?: boolean;
    /** If set to false, the return type will always be Response */
    parseResponse?: boolean;
    requestInit?: RequestInit;
};

/** A single JSON Schema validation failure nested under `error.errors[]`. */
export type FieldValidationError = {
    message: string;
    code?: string;
    params?: string[];
    /** Path into the submitted body that failed validation, e.g. ["products", 10, "name"]. */
    path?: Array<string | number>;
};

type ErrorResponse = {
    error: {
        message: string;
        code?: string;
        errors?: FieldValidationError[];
        path?: Array<string | number>;
    };
};

export type ApiError<T = ErrorResponse> = {
    response: Response;
    data: T;
    url: string;
    method: string;
};

export const toAuthorizationBearerHeader = (token: string) => {
    const headers: HttpHeaders = {
        Authorization: `Bearer ${token}`,
    };
    return headers;
};

const getSystemHeaders = () => {
    const headers: HttpHeaders = {
        "Dintero-System-Name": "backoffice",
        "Dintero-System-Version": `${VERSION || ""}`,
    };
    return headers;
};

const addCacheBustParam = (url: string) => {
    if (url.indexOf("?") > -1) {
        return `${url}&_v=${getCacheBusterValue()}`;
    }
    return `${url}?_v=${getCacheBusterValue()}`;
};

const getCacheBusterValue = () => {
    return new Date().valueOf() + Math.random().toString(36).substring(7);
};

const defaultParams: ApiParams<unknown, unknown> = {
    method: "GET",
    url: "",
    accountId: "",
    json: undefined,
    cacheBuster: true,
    includeSystemHeaders: true,
    signal: undefined,
    useErrorStore: true,
    parseResponse: true,
};

const authTokenExpired = (headers: Headers | undefined) => {
    try {
        const authToken = headers?.get("Authorization") || "";
        const [bearer, token] = authToken.split(" ");
        if (bearer === "Bearer") {
            const expiry = expiresAt(token || "");
            return expiry < new Date();
        }
        // try to inspect token
    } catch (e) {
        console.error(e);
    }
    return false;
};

export const isApiFetchError = (error: unknown): error is ApiError => {
    return (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        "data" in error
    );
};

const handleError = (error: unknown, useErrorStore: boolean | undefined) => {
    if (error instanceof Error) {
        errorExternalStore.dispatch("setError", {
            cause: Causes.FetchException,
            message: error.message,
        });
    }
    if (isApiFetchError(error)) {
        if (useErrorStore) {
            errorExternalStore.dispatch("setError", {
                cause: match(
                    error.response.status,
                    [404, () => Causes.NotFound],
                    [
                        undefined,
                        () =>
                            authTokenExpired(error.response.headers)
                                ? Causes.AccessTokenExpired
                                : Causes.UnhandledStatus,
                    ],
                ),
                status: error.response.status,
                statusText:
                    error.response.statusText ||
                    statusCodes[
                        error.response.status as keyof typeof statusCodes
                    ],
                message:
                    typeof error.data === "string"
                        ? error.data
                        : JSON.stringify(error.data),
                "request-id":
                    error.response.headers.get("request-id") || undefined,
                url: error.url,
                method: error.method,
            });
        }
        throw error;
    }
    throw error;
};

const apiImpl = async <T, P = unknown>(params: ApiParams<T, P>) => {
    params = {
        ...(defaultParams as ApiParams<T, P>),
        ...params,
    };
    const {
        method,
        url,
        headers,
        accountId,
        json,
        body,
        cacheBuster,
        includeSystemHeaders,
        signal,
        useErrorStore,
        parseResponse,
        requestInit,
    } = params;
    const fetchHeaders = new Headers(
        body
            ? {}
            : {
                  "Content-Type": "application/json; charset=utf-8",
                  Accept: "application/json",
              },
    );
    if (!url || !method) {
        throw handleError(new Error("Missing URL or method"), useErrorStore);
    }

    if (includeSystemHeaders) {
        const systemHeaders = getSystemHeaders();
        for (const key in systemHeaders) {
            fetchHeaders.set(key, systemHeaders[key]);
        }
    }

    if (accountId && accountId !== "none") {
        const token = await getAuthTokenForAccount(accountId);
        if (token) {
            const authorizationHeader = toAuthorizationBearerHeader(token);
            for (const key in authorizationHeader) {
                fetchHeaders.set(key, authorizationHeader[key]);
            }
        }
    }

    if (headers) {
        for (const key in headers) {
            fetchHeaders.set(key, headers[key]);
        }
    }

    const fetchUrl = cacheBuster ? addCacheBustParam(url) : url;
    const fetchParams: RequestInit = {
        method,
        headers: fetchHeaders,
        signal,
        ...requestInit,
    };

    if (json) {
        fetchParams.body = JSON.stringify(json);
    }

    if (body) {
        fetchParams.body = body;
    }

    const response = await fetch(fetchUrl, fetchParams);
    if (!parseResponse) {
        return response as T;
    }

    const contentType = response.headers.get("content-type");
    const data = contentType?.includes("application/json")
        ? await response.json()
        : contentType?.includes("application/pdf")
          ? await response.blob()
          : await response.text();

    if (!response.ok) {
        if (params.handlers?.[response.status]) {
            return params.handlers[response.status](
                data as T,
                response.headers,
                isErrorResponse(data) ? data : undefined,
            );
        }
        if (response.status === 404) {
            // Avoid using the error store for 404s by default, let the caller handle it
            throw handleError(
                {
                    response,
                    data,
                    url,
                    method,
                },
                false,
            );
        }
        throw handleError({ response, data, url, method }, useErrorStore);
    }

    if (params.handlers?.[response.status]) {
        return params.handlers[response.status](data as T, response.headers);
    }

    return data as T;
};

/** Options for the convenience methods — everything except `method`/`url`. */
type ApiMethodOptions<T, P = unknown> = Omit<ApiParams<T, P>, "method" | "url">;

interface Api {
    <T, P = unknown>(params: ApiParams<T, P>): Promise<T>;
    get<T>(url: string, options?: ApiMethodOptions<T>): Promise<T>;
    post<T, P = unknown>(
        url: string,
        options?: ApiMethodOptions<T, P>,
    ): Promise<T>;
    put<T, P = unknown>(
        url: string,
        options?: ApiMethodOptions<T, P>,
    ): Promise<T>;
    patch<T, P = unknown>(
        url: string,
        options?: ApiMethodOptions<T, P>,
    ): Promise<T>;
    delete<T>(url: string, options?: ApiMethodOptions<T>): Promise<T>;
}

/**
 * The single fetch wrapper. Call it with a params object — `api({ method, url, ... })` —
 * or use the method shorthands, e.g. `api.get(url, { accountId })`.
 */
export const api: Api = Object.assign(apiImpl, {
    get: <T>(url: string, options?: ApiMethodOptions<T>) =>
        apiImpl<T>({ ...options, method: "GET", url }),
    post: <T, P = unknown>(url: string, options?: ApiMethodOptions<T, P>) =>
        apiImpl<T, P>({ ...options, method: "POST", url }),
    put: <T, P = unknown>(url: string, options?: ApiMethodOptions<T, P>) =>
        apiImpl<T, P>({ ...options, method: "PUT", url }),
    patch: <T, P = unknown>(url: string, options?: ApiMethodOptions<T, P>) =>
        apiImpl<T, P>({ ...options, method: "PATCH", url }),
    delete: <T>(url: string, options?: ApiMethodOptions<T>) =>
        apiImpl<T>({ ...options, method: "DELETE", url }),
});

export const isErrorResponse = (data: unknown): data is ErrorResponse => {
    return (
        typeof data === "object" &&
        data !== null &&
        "error" in data &&
        typeof data.error === "object" &&
        data.error !== null &&
        ("message" in data.error || "code" in data.error)
    );
};
